import {
  computed,
  effect,
  inject,
  INJECTOR,
  Injector,
  linkedSignal,
  signal,
  WritableSignal,
} from '@angular/core';
import { MbscTimelineColumnWidth } from '@mobiscroll/angular';
import { routeParam } from '@softline/application';
import { IsoDate, startOf, toIsoDate, toIsoDateTime } from '@softline/core';
import { RosterFeatureRegistry } from '../services/roster-feature.registry';
import { RosterService } from '../services/roster.service';
import { CalendarViewType } from '../types/calendar-view-type';
import { DepartmentHead } from '../types/department-head';

/**
 * The main component that utilizes the feature registry to create and manage
 * the requested feature with the provided RosterFeature-Configuration and it's deps
 * All components provided in a RosterFeature configuration can inject this host
 */
export abstract class RosterHostComponent {
  readonly featureRegistry = inject(RosterFeatureRegistry);
  readonly injector = inject(INJECTOR);

  /**
   * Whether the resource view (content/sidebar) is hidden (minimized) or not
   * `true` means "hidden" (minimized)
   * `false` means "visible" (expanded)
   * Starts collapsed on small screens (< 768px)
   */
  readonly minimize = signal<boolean>(globalThis.innerWidth < 768);

  /**
   * The *main date* used for api-requests
   * This **must be the single-source-of-truth** for all calendar date related things
   */
  abstract date: WritableSignal<Date>;

  /**
   * Normalizes the date to start date of the current `calendarViewType` (date interval)
   * E.g.: type = "week" -> date will be normalized to the start of week using
   * the framework's `startOf()` method
   */
  readonly normalizedDate = linkedSignal(() => {
    const date = this.date();
    const viewType = this.calendarViewType();
    const isoDate = toIsoDateTime(date);

    if (viewType === 'planMonth') {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const from = this.planMonthFrom();
      const to = this.planMonthTo();
      if (now >= from && now <= to) {
        return now;
      }
      return from;
    }

    const startOfInterval = viewType === 'workWeek' ? 'week' : viewType;
    const periodStart = new Date(startOf(isoDate, startOfInterval));

    if (viewType === 'month') {
      const now = new Date();
      if (
        periodStart.getFullYear() === now.getFullYear() &&
        periodStart.getMonth() === now.getMonth()
      ) {
        return new Date(now.getFullYear(), now.getMonth(), now.getDate());
      }
    }

    return periodStart;
  });

  private static readonly VIEW_TYPE_STORAGE_KEY = 'roster_calendar_view_type';

  /**
   * The currently displayed date-interval of the calendar (e.g. day, week, month)
   */
  readonly calendarViewType = signal<CalendarViewType>(
    (localStorage.getItem(RosterHostComponent.VIEW_TYPE_STORAGE_KEY) as CalendarViewType) || 'week'
  );

  private readonly _persistViewType = effect(() => {
    localStorage.setItem(RosterHostComponent.VIEW_TYPE_STORAGE_KEY, this.calendarViewType());
  });

  /** Maps CalendarViewType to DateNavigatorInterval (workWeek → week) */
  readonly dateNavigatorInterval = computed(() => {
    const vt = this.calendarViewType();
    return vt === 'workWeek' ? 'week' : vt;
  });

  /**
   * Custom date range bounds used by the 'planMonth' view.
   * Updated by the date-range-navigator when the user picks a range.
   */
  readonly planMonthFrom = signal<Date>((() => {
    const d = new Date(); d.setHours(0, 0, 0, 0);
    const day = d.getDay(); d.setDate(d.getDate() - (day === 0 ? 6 : day - 1) - 7);
    return d;
  })());
  readonly planMonthTo = signal<Date>((() => {
    const d = new Date(); d.setHours(0, 0, 0, 0);
    const day = d.getDay(); d.setDate(d.getDate() + (day === 0 ? 0 : 7 - day) + 14);
    return d;
  })());

  readonly planMonthFromIso = computed<IsoDate>(() => toIsoDate(this.planMonthFrom()));
  readonly planMonthToIso = computed<IsoDate>(() => toIsoDate(this.planMonthTo()));

  resetPlanMonth = (): void => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const day = today.getDay();

    const from = new Date(today);
    from.setDate(today.getDate() - (day === 0 ? 6 : day - 1) - 7);
    this.planMonthFrom.set(from);

    const to = new Date(today);
    to.setDate(today.getDate() + (day === 0 ? 0 : 7 - day) + 14);
    this.planMonthTo.set(to);
  };

  setPlanMonthFrom(iso: IsoDate): void {
    const [y, m, d] = iso.split('-').map(Number);
    this.planMonthFrom.set(new Date(y, m - 1, d));
  }

  setPlanMonthTo(iso: IsoDate): void {
    const [y, m, d] = iso.split('-').map(Number);
    this.planMonthTo.set(new Date(y, m - 1, d));
  }

  /**
   * The width of the mobiscroll calendar columns
   */
  readonly calendarColumnWidth = signal<MbscTimelineColumnWidth>('xxxlarge');

  /**
   * The amount of data displayed in the calendars content (events, resources)
   */
  readonly calendarDetails = signal<'compact' | 'all'>('compact');
  /**
   * The event type filters that are currently active. This is used to filter the events displayed in the calendar
   */
  readonly eventTypesFilter = signal<string[]>(['appointments', 'holidays', 'tickets']);

  /**
   * The feature name that will be used to obtain the RosterFeature
   * config from the registry
   */
  readonly featureName = routeParam('featureName');

  /**
   * Returns the registered feature returned from the feature registry
   * If no registered feature is found `null` will be returned
   */
  readonly feature = computed(() => {
    const name = this.featureName();
    return name ? (this.featureRegistry.get(name) ?? null) : null;
  });

  /**
   * Returns the custom injector specifically created for the current feature
   * All providers of the feature will be declared in this injector.
   * Each provider is available in every component declared in the RosterFeature
   */
  readonly featureInjector = computed(() => {
    return Injector.create({
      parent: this.injector,
      providers: [...(this.feature()?.providers ?? [])],
    });
  });

  /**
   * Returns the feature specific RosterService instance
   */
  readonly featureService = computed(() => {
    return this.featureInjector().get(RosterService);
  });

  /**
   * Whether any calendar content is loading (events, structure, resources)
   */
  readonly calendarContentLoading = computed(() =>
    this.featureService().calendarContentLoading()
  );

  onDepartmentHeadChange?: (value: DepartmentHead) => Promise<void>; 
}
